"use client";

import React from "react";
import Link from "next/link";

import HyperlinkIcon from "@/components/icons/HyperlinkIcon";
import ExternalIcon from "@/components/icons/ExternalIcon";

import { trackInteraction } from "@/utils/trackInteraction";
import { getCookieValue } from "@/utils/getCookieValue";
import { getMetaData } from "@/utils/getMetaData";
import { useLocale } from "@/components/providers/LocaleProvider";
import {
  DEFAULT_LOCALE,
  LOCALE_SWITCHER_ENABLED,
} from "@/utils/localeConstants";

function extractTextContent(node: React.ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractTextContent).join(" ");
  if (React.isValidElement(node)) {
    const el = node as React.ReactElement<{ children?: React.ReactNode }>;
    return extractTextContent(el.props.children ?? "");
  }
  return "";
}

function labelFromPath(href: string): string {
  try {
    const path = new URL(href, "http://x").pathname;
    return path
      .replace(/\/$/, "")
      .split("/")
      .filter(Boolean)
      .map((s) => s.replace(/-/g, " "))
      .join(" - ");
  } catch {
    return href;
  }
}

interface NextLinkProps {
  href?: any;
  target?: string;
  tabIndex?: number;
  type?: string;
  text?: string;
  title?: string;
  rel?: string;
  slot?: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  children?: React.ReactNode;
  isHyperlink?: boolean;
  showIcons?: boolean;
  isLink?: boolean;
  requestPerms?: boolean;
  className?: string;
  trackingData?: { [key: string]: any };
  "aria-label"?: string;
}

const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL;

/**
 * Prepends the locale prefix to internal paths when the active locale is not
 * the default. External links and anchor-only links are returned unchanged.
 *
 * In direct-URL-only mode (LOCALE_SWITCHER_ENABLED=false) this always returns
 * the unmodified href so all navigation defaults to the English base path.
 */
function localizeHref(href: string, locale: string): string {
  if (!LOCALE_SWITCHER_ENABLED) return href;
  if (locale === DEFAULT_LOCALE) return href;
  if (!href || href.startsWith("#") || href.includes("//")) return href;
  const prefix = `/${locale}`;
  return href.startsWith(prefix)
    ? href
    : `${prefix}${href.startsWith("/") ? href : `/${href}`}`;
}

export default function NextLink({
  href = "#",
  target,
  tabIndex = 0,
  type = "link",
  text = "Image",
  title,
  rel = undefined,
  slot,
  onClick,
  children,
  isHyperlink,
  showIcons,
  isLink,
  requestPerms,
  className,
  trackingData = {},
  "aria-label": ariaLabel,
}: NextLinkProps) {
  const locale = useLocale();
  const rawHref =
    href?.includes(BASE_URL) && target && target !== "_blank"
      ? href?.replace(BASE_URL, "/")
      : href;
  const cleanHref = localizeHref(rawHref, locale);
  const isExternal = cleanHref?.includes("//");
  const linkPaths = [
    "/resource-library",
    "/resource-library/?type=case-studies",
    "/resource-library/?type=analyst-report",
  ];

  const handleTarget = () => {
    if (target) return target;
    if (isExternal) return "_blank";
    return "_self";
  };

  const getElementPosition = (
    element: HTMLElement | null,
  ): { top: number; left: number } => {
    if (!element) return { top: 0, left: 0 };
    const rect = element.getBoundingClientRect();
    return {
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
    };
  };

  const getScrollDepth = (): number => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight,
      document.body.clientHeight,
      document.documentElement.clientHeight,
    );
    return Math.round((scrollTop / docHeight) * 100);
  };

  const getDeviceType = (): string => {
    const ua = navigator.userAgent;
    if (/Mobi|Android/i.test(ua)) return "mobile";
    if (/Tablet|iPad/i.test(ua)) return "tablet";
    return "desktop";
  };

  const getGeolocation = (): Promise<Partial<GeolocationCoordinates>> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({});
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          });
        },
        () => resolve({}),
        { timeout: 1000 },
      );
    });
  };

  const getPermissions = async (): Promise<Record<string, string>> => {
    const permissions: Record<string, string> = {};
    const types = [
      "geolocation",
      "notifications",
      "camera",
      "microphone",
      "clipboard-read",
    ];

    for (const name of types) {
      try {
        const result = await navigator.permissions.query({
          name: name as PermissionName,
        });
        permissions[name] = result.state;
      } catch {
        permissions[name] = "unsupported";
      }
    }

    return permissions;
  };

  const handleOnclick = async (event: React.MouseEvent<HTMLElement>) => {
    const element = event.currentTarget as HTMLElement;

    const interactionDetails: Record<string, any> = {
      link_text: element.textContent?.trim() || text,
      link_url: cleanHref,
      link_type: type,
      page_path: window.location.pathname,
      page_url: window.location.href,
      element_position: getElementPosition(element),
      scroll_depth_percent: getScrollDepth(),
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      pixel_ratio: window.devicePixelRatio,
      color_depth: window.screen.colorDepth,
      language: navigator.language,
      user_agent: navigator.userAgent,
      platform: navigator.platform,
      device_type: getDeviceType(),
      device_memory: (navigator as any).deviceMemory || "unknown",
      hardware_concurrency: navigator.hardwareConcurrency || "unknown",
      cookies_enabled: navigator.cookieEnabled,
      js_enabled: true,
      do_not_track: navigator.doNotTrack,
      timestamp: new Date().toISOString(),
      identity: getCookieValue("hubspotutk"),
      content_tags: getMetaData("content-tag"),
      click_target_tag: element.tagName,
      click_target_classes: element.className,
      click_target_aria_label: element.getAttribute("aria-label"),
      click_target_alt_text: element.getAttribute("alt"),
      mouse_coordinates: {
        x: event.pageX,
        y: event.pageY,
      },
    };

    // Network info
    const connection = (navigator as any).connection || {};
    interactionDetails.connection_type = connection.effectiveType || "unknown";
    interactionDetails.downlink = connection.downlink || "unknown";
    interactionDetails.rtt = connection.rtt || "unknown";
    interactionDetails.save_data_mode = connection.saveData || false;

    // Fetch async data
    const [geo, perms] = requestPerms
      ? await Promise.all([getGeolocation(), getPermissions()])
      : [];

    trackInteraction(
      {
        ...interactionDetails,
        ...geo,
        permissions: perms,
        ...trackingData,
      },
      `${type}_click`,
    );

    if (onClick) onClick(event);

    const normalize = (p: string) => p.replace(/\/$/, "") || "/";
    const targetPath = normalize(
      new URL(cleanHref, window.location.origin).pathname,
    );
    if (targetPath === normalize(window.location.pathname)) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  };

  const Element = isLink || linkPaths?.includes(cleanHref) ? "a" : Link;

  const { resolvedAriaLabel, srOnlyContext } = (() => {
    if (ariaLabel) return { resolvedAriaLabel: ariaLabel, srOnlyContext: null };
    const visibleText = extractTextContent(children).trim();
    const wordCount = visibleText.split(/\s+/).filter(Boolean).length;
    const pathLabel = labelFromPath(cleanHref);
    if (wordCount > 0 && wordCount <= 3 && pathLabel) {
      return {
        resolvedAriaLabel: `${visibleText}: ${pathLabel}`,
        // Injected into DOM so Lighthouse sees descriptive text beyond "Learn more" / "More".
        srOnlyContext: `: ${pathLabel}`,
      };
    }
    // Children rendered in Stencil Shadow DOM are invisible to extractTextContent.
    // Add a sr-only span so Lighthouse link-text audit sees a descriptive string,
    // but don't set aria-label — the Stencil component provides its own accessible name.
    if (wordCount === 0 && pathLabel && cleanHref !== "#") {
      return {
        resolvedAriaLabel: undefined,
        srOnlyContext: pathLabel,
      };
    }
    return { resolvedAriaLabel: undefined, srOnlyContext: null };
  })();

  return (
    <Element
      slot={slot}
      href={cleanHref}
      target={handleTarget()}
      title={title}
      rel={rel}
      tabIndex={tabIndex}
      className={className}
      aria-label={resolvedAriaLabel}
      onClick={handleOnclick}
    >
      {children}
      {srOnlyContext && <span className="sr-only">{srOnlyContext}</span>}
      {showIcons &&
        (isHyperlink ? (
          <span className="inline-hyperlink">
            <HyperlinkIcon />
          </span>
        ) : (
          isExternal && (
            <span className="inline-block">
              <ExternalIcon />
            </span>
          )
        ))}
    </Element>
  );
}
